<template>
    <div class="mobile-combined-menu">
        <a
            href="#"
            tabindex="0"
            :aria-label="menuAriaLabel"
            class="mobile-combined-menu__trigger"
            :aria-expanded="shown"
            @keydown.enter="showMenu"
            @click="showMenu">
            <SkyIconBars
                decorative
                variant="brand">
            </SkyIconBars>
        </a>

        <SkyModalDialog
            id="mobile-combined-menu"
            v-model:visible="shown"
            :aria-label="menuAriaLabel"
            :class="dialogClasses"
            data-test="mobile-combined-menu">
            <template #header>
                <div
                    class="mobile-combined-menu__header"
                    data-test="menu-header">
                    <div class="mobile-combined-menu__header-user gap-400 u-flex u-items-center">
                        <UserAvatar
                            :user="userMenu.user"
                            size="sm">
                        </UserAvatar>
                        <SkyDisplay
                            variant="xs"
                            class="mobile-combined-menu__header-alias"
                            data-test="user-alias">
                            {{ userMenu.user.alias }}
                        </SkyDisplay>
                    </div>
                </div>
            </template>

            <template #default>
                <nav
                    :aria-label="menuAriaLabel"
                    class="mobile-combined-menu__nav"
                    data-test="nav">
                    <ul
                        class="mobile-combined-menu__list mobile-combined-menu__list--nav"
                        data-test="nav-list">
                        <li
                            v-for="item in navLinkItems"
                            :key="item.id"
                            class="mobile-combined-menu__item">
                            <a
                                :href="item.href"
                                :target="item.target"
                                :rel="item.rel"
                                class="mobile-combined-menu__link"
                                :class="{ 'mobile-combined-menu__link--active': item.active }"
                                @click="onLinkClick($event, item)">
                                {{ item.label }}
                            </a>
                        </li>
                    </ul>

                    <ul
                        v-if="userMenuItems.length"
                        class="mobile-combined-menu__list mobile-combined-menu__list--user"
                        data-test="user-list">
                        <li
                            v-for="item in userMenuItems"
                            :key="item.id"
                            class="mobile-combined-menu__item">
                            <a
                                :href="item.href"
                                :target="item.target"
                                :rel="item.rel"
                                class="mobile-combined-menu__link mobile-combined-menu__link--with-icon"
                                @click="onLinkClick($event, item)">
                                <span class="mobile-combined-menu__icon-wrapper">
                                    <component
                                        :is="item.icon"
                                        variant="brand"
                                        size="xs"
                                        class="mobile-combined-menu__link-icon"
                                        decorative>
                                    </component>
                                    <span
                                        v-if="item.hasNotifications"
                                        class="mobile-combined-menu__badge"
                                        :aria-label="badgeAriaLabel"
                                        data-test="notification-badge">
                                    </span>
                                </span>
                                {{ item.label }}
                            </a>
                        </li>
                    </ul>
                </nav>
            </template>
        </SkyModalDialog>
    </div>
</template>

<script lang="ts">
    import type { IDesktopUserInfoMenuConfig } from "@eg_ui/src/components/globals/DesktopUserInfoMenu/DesktopUserInfoMenu.vue";
    import type { IActionsConfig } from "@eg_ui/src/components/navigation/NavActions/NavActions.vue";
    import type { INavLink } from "@eg_ui/src/components/navigation/NavLinks/NavLinks.vue";
    import type { Component } from "vue";

    export interface IMobileCombinedMenuProps {
        links: INavLink[];
        userMenu: IDesktopUserInfoMenuConfig;
        actions: IActionsConfig;
        /** When true, the menu will be open on initial render (useful for Storybook/Chromatic) */
        initiallyOpen?: boolean;
    }

    export interface INavigationItem {
        id: string;
        label: string;
        href: string;
        active?: boolean;
        target?: string;
        rel?: string;
    }

    export interface IUserMenuItem extends INavigationItem {
        icon: Component;
        hasNotifications?: boolean;
    }
</script>

<script setup lang="ts">
    import UserAvatar from "@eg_ui/src/components/globals/UserAvatar/UserAvatar.vue";
    import { globalMessages } from "@eg_ui/src/translations/globals";
    import { injectI18n } from "@eg_ui/src/translations/i18n";
    import { GLOBAL_EVENTS, globalEventHandler } from "@eg_ui/src/utils/libs";
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import {
        SkyIconBars,
        SkyIconCircleQuestion,
        SkyIconExternalLink,
        SkyIconFavorite,
        SkyIconLogout,
        SkyIconNotification,
        SkyIconSettings,
        SkyIconUser,
        SkyIconUsers,
        SkyModalDialog,
    } from "@skyline2/vue";

    const props = defineProps<IMobileCombinedMenuProps>();
    const intl = injectI18n();

    const shown = ref<boolean>(false);

    // Open the menu initially if the prop is set (useful for Storybook/Chromatic)
    onMounted(() => {
        if (props.initiallyOpen) {
            shown.value = true;
        }
    });

    // Ensure body class is reset when modal closes via external actions
    watch(shown, (isOpen) => {
        if (!isOpen) {
            document.body.classList.remove("modal-open");
        } else {
            document.body.classList.add("modal-open");
        }
    });

    onBeforeUnmount(() => {
        document.body.classList.remove("modal-open");
    });

    const dialogClasses = computed<Record<string, boolean>>(() => ({
        fade: shown.value,
        "mobile-combined-menu__dialog": true,
    }));

    const LOGOUT_ITEM_ID = "logout";

    function showMenu() {
        shown.value = true;
    }

    function hide() {
        shown.value = false;
    }

    const menuAriaLabel = computed<string>(() => {
        let label = intl.formatMessage({
            defaultMessage: "Navigation menu",
            id: "globals.mobileCombinedMenu.ariaLabel",
        });

        if (props.userMenu?.hasNotifications) {
            label += intl.formatMessage({
                defaultMessage: ", has notifications",
                id: "globals.mobileCombinedMenu.ariaLabelWithNotifications",
            });
        }

        return label;
    });

    const badgeAriaLabel = computed<string>(() => {
        return intl.formatMessage({
            defaultMessage: "Unread",
            id: "globals.mobileCombinedMenu.notificationBadgeAriaLabel",
        });
    });

    const navLinkItems = computed<INavigationItem[]>(() => {
        const items: INavigationItem[] = [];

        if (props.links) {
            for (const link of props.links) {
                // Skip admin links
                if (link.isAdmin) {
                    continue;
                }
                items.push({
                    active: link.active,
                    href: link.href,
                    id: link.key,
                    label: link.label,
                    ...(link.external ? { rel: "noopener noreferrer", target: "_blank" } : {}),
                });
            }
        }

        // Search
        if (props.actions?.searchUrl) {
            items.push({
                href: props.actions.searchUrl,
                id: "search",
                label: intl.formatMessage(globalMessages.searchPlaceholder),
            });
        }

        return items;
    });

    // User menu items (with icons)
    const userMenuItems = computed<IUserMenuItem[]>(() => {
        const items: IUserMenuItem[] = [];

        if (!props.userMenu?.isAuthenticated) {
            return items;
        }

        // Group Leader Dashboard
        if (props.userMenu.leaderDashboard?.enabled && props.userMenu.leaderDashboard.url) {
            items.push({
                href: props.userMenu.leaderDashboard.url,
                icon: SkyIconUsers,
                id: "leader-dashboard",
                label: intl.formatMessage(globalMessages.leaderDashboard),
            });
        }

        // My Profile
        items.push({
            href: props.userMenu.urls.profile,
            icon: SkyIconUser,
            id: "profile",
            label: intl.formatMessage({
                defaultMessage: "My Profile",
                id: "globals.mobileCombinedMenu.myProfile",
            }),
        });

        // Notifications
        items.push({
            hasNotifications: props.userMenu.hasNotifications,
            href: props.userMenu.urls.notifications,
            icon: SkyIconNotification,
            id: "notifications",
            label: intl.formatMessage(globalMessages.notifications),
        });

        // Get Started
        items.push({
            href: props.userMenu.urls.welcome,
            icon: SkyIconFavorite,
            id: "get-started",
            label: intl.formatMessage(globalMessages.getStarted),
        });

        // Spark Program Link
        if (props.userMenu.sparkProgramName && props.userMenu.urls.spark_url) {
            items.push({
                href: props.userMenu.urls.spark_url,
                icon: SkyIconExternalLink,
                id: "spark",
                label: props.userMenu.sparkProgramName,
                rel: "noopener noreferrer",
                target: "_blank",
            });
        }

        // Settings
        items.push({
            href: props.userMenu.urls.settings,
            icon: SkyIconSettings,
            id: "settings",
            label: intl.formatMessage(globalMessages.settings),
        });

        // Help
        if (props.actions?.helpUrl) {
            items.push({
                href: props.actions.helpUrl,
                icon: SkyIconCircleQuestion,
                id: "help",
                label: intl.formatMessage({
                    defaultMessage: "Help",
                    id: "globals.mobileCombinedMenu.help",
                }),
            });
        }

        // Logout
        items.push({
            href: "#",
            icon: SkyIconLogout,
            id: LOGOUT_ITEM_ID,
            label: intl.formatMessage(globalMessages.logOut),
        });

        return items;
    });

    function onLinkClick(event: Event, item: INavigationItem) {
        if (item.id === LOGOUT_ITEM_ID) {
            event.preventDefault();
            globalEventHandler.emit(GLOBAL_EVENTS.ACCOUNT.LOGOUT);
        }
        hide();
    }
</script>

<style lang="scss">
    @use "./MobileCombinedMenu.scss";
</style>
